import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { uk } from "date-fns/locale";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Clock,
  FileText,
  Lock,
  RotateCcw,
  Undo2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/cn";
import {
  fetchMeetingsControl,
  type ControlItem,
  type ControlView,
} from "@/api/meetingsControl";
import { ReviewReportDrawer } from "./ReviewReportDrawer";
import { ApiClientError } from "@/api/client";

/**
 * D-055 S5 — «Контроль команди» всередині /meetings (visual v0.2 екран 5).
 *
 * Role-gated (рендериться лише при controlAllowed; endpoint 403-ить менеджера).
 * 5 видів, backend-owned counts, RISK-first. Review вибірковий, не bottleneck.
 * Керівник НЕ редагує debrief · self-review заборонено (S1 enforce + UI).
 */

const VIEWS: Array<{ k: ControlView; label: string; danger?: boolean }> = [
  { k: "pending_review", label: "На перевірці" },
  { k: "no_submission", label: "Без подання" },
  { k: "overdue", label: "Прострочені", danger: true },
  { k: "no_next_action", label: "Без наступної дії" },
  { k: "returned", label: "Повернуті на уточнення" },
];

export function SupervisorControlPanel() {
  const qc = useQueryClient();
  const [view, setView] = useState<ControlView>("pending_review");
  const [selected, setSelected] = useState<ControlItem | null>(null);

  const q = useQuery({
    queryKey: ["meetingsControl", view],
    queryFn: ({ signal }) => fetchMeetingsControl({ view }, signal),
    staleTime: 30_000,
  });

  const counts = q.data?.counts;

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["meetingsControl"] });
    void qc.invalidateQueries({ queryKey: ["meetingsQueue"] });
  };

  return (
    <>
      {/* 5 видів — chips з backend counts */}
      <div className="flex flex-wrap items-center gap-2">
        {VIEWS.map((v) => {
          const c = counts?.[v.k];
          return (
            <button
              key={v.k}
              type="button"
              onClick={() => setView(v.k)}
              className={cn(
                "rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                view === v.k
                  ? "border-primary bg-primary/10 text-primary"
                  : v.danger && (c ?? 0) > 0
                    ? "border-destructive/40 text-destructive hover:bg-destructive/5"
                    : "border-border text-muted-foreground hover:text-foreground",
              )}
            >
              {v.label}
              {typeof c === "number" && c > 0 && (
                <span className={cn("ml-1", v.danger && "text-destructive")}>· {c}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* D-151 S2 (P19) — лічильник «заповнено керівником» (Q-I3=A): subset
          submitted-KPI, той самий team-scope, що сусідні counts */}
      {typeof counts?.on_behalf_submitted === "number" && counts.on_behalf_submitted > 0 && (
        <p className="px-1 text-[11.5px] text-muted-foreground">
          Заповнено керівником за менеджерів:{" "}
          <b className="text-foreground">{counts.on_behalf_submitted}</b> (звіт у менеджера
          «є», атрибуція керівника незмінна)
        </p>
      )}

      {q.isPending ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-[72px]" />
          ))}
        </div>
      ) : q.isError ? (
        q.error instanceof ApiClientError && q.error.status === 403 ? (
          /* S6: no_access/no_team ≠ generic error (impl-state supervisor) */
          <div className="rounded-lg border bg-muted/30 p-8 text-center">
            <Lock className="mx-auto mb-2 h-5 w-5 text-muted-foreground" aria-hidden />
            <p className="text-sm font-medium">Розділ доступний лише керівникам команд</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Потрібна роль supervisor/director і команда (NULL=DENY).
            </p>
          </div>
        ) : (
          <div className="rounded-lg border bg-destructive/5 p-6 text-center">
            <AlertCircle className="mx-auto mb-2 h-5 w-5 text-destructive" />
            <p className="text-sm text-destructive">
              {(q.error as Error)?.message ?? "Не вдалося завантажити"}
            </p>
            <Button size="sm" variant="outline" className="mt-3 gap-1.5" onClick={() => void q.refetch()}>
              <RotateCcw className="h-3.5 w-3.5" /> Спробувати ще
            </Button>
          </div>
        )
      ) : (q.data?.items.length ?? 0) === 0 ? (
        <div className="rounded-lg border bg-muted/30 p-8 text-center">
          <CheckCircle2 className="mx-auto mb-2 h-5 w-5 text-muted-foreground" aria-hidden />
          <p className="text-sm font-medium">У цьому виді порожньо</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {view === "no_submission" || view === "overdue"
              ? "Команда подає звіти вчасно."
              : "Немає звітів, що потребують уваги."}
          </p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {q.data!.items.map((m) => (
            <ControlRow key={m.id} m={m} onOpen={() => setSelected(m)} />
          ))}
        </div>
      )}

      <p className="px-1 text-[11px] text-muted-foreground">
        Team-scope (команда керівника) · review вибірковий, не bottleneck · керівник не
        редагує підсумок менеджера · self-review заборонено · return → сповіщення менеджеру (D-043).
      </p>

      <ReviewReportDrawer
        item={selected}
        onClose={() => setSelected(null)}
        onDone={invalidate}
      />
    </>
  );
}

function ControlRow({ m, onOpen }: { m: ControlItem; onOpen: () => void }) {
  const overdue = !!m.reportOverdueSince && !m.reportSubmitted;

  const fmt = (v: string | null, pattern = "dd.MM HH:mm") => {
    if (!v) return null;
    try {
      return format(parseISO(v.replace(" ", "T")), pattern, { locale: uk });
    } catch {
      return null;
    }
  };

  const statePill = overdue ? (
    <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-[10.5px] font-bold text-destructive">
      звіт прострочено
    </span>
  ) : !m.reportSubmitted ? (
    <span className="rounded-full bg-warning px-2 py-0.5 text-[10.5px] font-bold text-warning-foreground">
      звіт без подання
    </span>
  ) : m.reviewStatus === "returned" ? (
    <span className="rounded-full bg-warning px-2 py-0.5 text-[10.5px] font-bold text-warning-foreground">
      повернуто на уточнення
    </span>
  ) : m.reviewStatus === "accepted" ? (
    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10.5px] font-bold text-primary">
      прийнято
    </span>
  ) : (
    <span className="rounded-full bg-muted px-2 py-0.5 text-[10.5px] font-bold text-muted-foreground">
      подано
    </span>
  );

  const Icon = overdue ? Clock : m.reportSubmitted ? FileText : Undo2;

  return (
    <div className="flex min-h-[72px] items-center gap-3 rounded-xl border bg-card px-3.5 py-3">
      <span
        className={cn(
          "grid h-10 w-10 shrink-0 place-items-center rounded-lg",
          overdue || m.risk
            ? "bg-destructive/10 text-destructive"
            : "bg-muted text-muted-foreground",
        )}
      >
        <Icon className="h-4.5 w-4.5" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5 text-sm font-semibold">
          <span className="truncate">{m.summary}</span>
          {statePill}
          {m.risk && (
            <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 text-[10.5px] font-bold text-destructive">
              <AlertTriangle className="h-3 w-3" aria-hidden /> risk-результат
            </span>
          )}
          {m.reportSubmissionMode === "supervisor_on_behalf" && (
            <span className="rounded-full bg-violet px-2 py-0.5 text-[10.5px] font-bold text-violet-foreground">
              заповнив керівник{m.reportSubmittedByName ? `: ${m.reportSubmittedByName}` : ""}
            </span>
          )}
        </div>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">
          {[
            m.managerName ? `Менеджер: ${m.managerName}` : null,
            m.accountName,
            m.occurredAt ? `проведено ${fmt(m.occurredAt)}` : null,
            !m.reportSubmitted && m.reportSlaDueAt && !overdue
              ? `звіт до ${fmt(m.reportSlaDueAt)}`
              : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        <button
          type="button"
          onClick={onOpen}
          className="rounded-md border border-border px-2.5 py-1.5 text-xs font-semibold hover:bg-muted"
        >
          Відкрити звіт
        </button>
        {m.allowedActions.canReturn && (
          <button
            type="button"
            onClick={onOpen}
            className="rounded-md border border-border px-2.5 py-1.5 text-xs font-semibold hover:bg-muted"
          >
            Повернути на уточнення
          </button>
        )}
      </div>
    </div>
  );
}
